/* import __COLOCATED_TEMPLATE__ from './similar-conversations.hbs'; */
/* RESPONSIBLE TEAM: team-inbox-experience */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import type Router from '@ember/routing/router-service';
import { tracked } from '@glimmer/tracking';
import type Session from 'embercom/services/session';
import type Conversation from 'embercom/objects/inbox/conversation';
import { ConversationState } from 'embercom/objects/inbox/conversation';
import type SidebarSection from 'embercom/objects/inbox/sidebar-section';
import type Inbox from 'embercom/objects/inbox/inboxes/inbox';
import type InboxApi from 'embercom/services/inbox-api';
import type LatestConversationSummary from 'embercom/objects/inbox/latest-conversation-summary';
import type RenderablePart from 'embercom/objects/inbox/renderable-part';
import { type SupportedSidebarSection } from 'embercom/services/inbox-sidebar-service';
import type InboxSidebarService from 'embercom/services/inbox-sidebar-service';
import type InboxState from 'embercom/services/inbox-state';
import { helper } from '@ember/component/helper';
import { DeduplicatedAsyncData } from 'embercom/resources/utils/async-data';
import { type UpdateMessage } from 'embercom/services/conversation-updates';
import type ConversationUpdates from 'embercom/services/conversation-updates';
import type ApplicationInstance from '@ember/application/instance';
import { type SidebarSectionComponentArgs } from 'embercom/lib/inbox2/sidebar-types';
import { InboxType } from 'embercom/models/data/inbox/inbox-types';
import { InboxCategory } from 'embercom/models/data/inbox/inbox-categories';
import safeWindowOpen from 'embercom/lib/safe-window-open';

type Args = SidebarSectionComponentArgs<{
  conversation: Conversation;
  sidebarSection: SidebarSection;
  activeInbox?: Inbox;
  onExpandedSectionChange: (sectionId: SupportedSidebarSection | number | undefined) => void;
  previewOnly?: boolean;
}>;

interface Signature {
  Args: Args;
}

export default class SimilarConversations extends Component<Signature> {
  @service declare session: Session;
  @service declare router: Router;
  @service declare intercomEventService: any;
  @service declare inboxApi: InboxApi;
  @service declare inboxState: InboxState;
  @service declare inboxSidebarService: InboxSidebarService;
  @service declare conversationUpdates: ConversationUpdates;

  @tracked shouldLoadSimilarConversations = false;

  constructor(owner: ApplicationInstance, args: Args) {
    super(owner, args);
    this.conversationUpdates.subscribe(this.updateConversations);
  }

  willDestroy(): void {
    super.willDestroy();
    this.conversationUpdates.unsubscribe(this.updateConversations);
  }

  get similarConversations() {
    return this.sortSimilarConversations(this.similarConversationsLoader.value ?? []);
  }

  get limitedSimilarConversations() {
    return this.similarConversations.slice(0, 3);
  }

  @action handleConversationClick(conversation: LatestConversationSummary) {
    this.intercomEventService.trackAnalyticsEvent({
      action: 'opened',
      section: 'inbox',
      object: 'similar_conversations',
      inbox_type: this.args.activeInbox?.type,
      conversation_id: conversation.id,
    });

    if (this.args.previewOnly) {
      let inbox = this.inboxState.activeInbox || {
        id: InboxType.All,
        category: InboxCategory.Shared,
      };

      let url: string = this.router.urlFor(
        'inbox.workspace.inbox.inbox.conversation.conversation',
        inbox.category,
        inbox.id,
        conversation.id,
      );
      safeWindowOpen(url);
    } else {
      this.inboxSidebarService.previewConversation(conversation);
    }

    return;
  }

  getSummaryPart = helper(function ([conversation]: [LatestConversationSummary]):
    | RenderablePart
    | undefined {
    return conversation.state === ConversationState.Closed
      ? conversation.firstUserPart
      : conversation.lastRenderableSummaryPart;
  });

  sortSimilarConversations(
    conversations: LatestConversationSummary[],
  ): LatestConversationSummary[] {
    return [...conversations].sort(
      (conv1: LatestConversationSummary, conv2: LatestConversationSummary) => {
        if (conv1.state === conv2.state) {
          if (conv1.lastUpdated < conv2.lastUpdated) {
            return 1;
          } else if (conv1.lastUpdated > conv2.lastUpdated) {
            return -1;
          } else {
            return 0;
          }
        } else {
          return conv1.state === ConversationState.Open ? -1 : 1;
        }
      },
    );
  }

  @action
  private updateConversations(updates: UpdateMessage[]) {
    if (!this.shouldLoadSimilarConversations) {
      return;
    }
    updates.forEach((update) => {
      let conversation = this.similarConversations.find((c) => c.id === update.conversationId);
      if (!conversation) {
        return;
      }

      if (update.type === 'added') {
        update.entries.forEach((update) => update.apply(conversation));
      } else if (update.type === 'removed') {
        update.entries.forEach((update) => update.rollback(conversation));
      }
    });
  }

  private similarConversationsLoader = DeduplicatedAsyncData(
    this,
    () => [this.args.conversation.id],
    async (conversationId, { signal }) => {
      let { conversations } = await this.inboxApi.getSimilarConversations(
        conversationId.toString(),
        { signal },
      );

      return conversations;
    },
  );
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::ConversationDetailsSidebar::SidebarSections::SimilarConversations': typeof SimilarConversations;
    'inbox2/conversation-details-sidebar/sidebar-sections/similar-conversations': typeof SimilarConversations;
  }
}
